import { ChangeDetectionStrategy, Component, HostBinding, Input, Optional, SkipSelf } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { BehaviorSubject } from 'rxjs';
import { ExportDialogComponent, ExportDialogPage, FileTypes } from 'src/app/dialogs/export-dialog/export-dialog.component';
import { ExportOptions } from 'src/app/dialogs/export-dialog/store/model';
import { ExportProgressDialogComponent } from 'src/app/dialogs/export-progress-dialog';
import { AmpTrackCtx } from 'src/app/services/tracking/amp-track';
import { TrackingContext, provideTrackingCtx } from 'src/app/services/tracking/context';
import { TrackingService } from 'src/app/services/tracking/service';

@Component({
  selector: 'app-export-options-button',
  templateUrl: './export-options-button.component.html',
  styleUrls: ['./export-options-button.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    provideTrackingCtx({}),
  ],
})
export class ExportOptionsButtonComponent<TParams = any> {
  @Input() page: ExportDialogPage;
  @Input() hideLabel = false;
  @Input() showUserFieldsColumn: boolean;
  @Input() showContentColumn: boolean;
  @Input() showOrganizationColumn: boolean;
  @Input() exportHandler: (exportOptions: ExportOptions<TParams>) => void;
  @Input() exportProgress: BehaviorSubject<number>;
  @Input() customFootnote: string;
  @Input() fileTypes: FileTypes;
  @Input() params?: TParams;
  @Input() disabledForUpsell = false;

  /**
   * Sub-nav export button identity. Tagged here (not per-page) so every report
   * sub-nav emits `export-button` consistently. Context resolves from the
   * nearest `AmpTrackCtx` (page or app shell).
   */
  @HostBinding('attr.data-amp-track-component') readonly ampTrackComponent = 'export-button';
  @HostBinding('attr.data-amp-track-context') get ampTrackContextAttr() {
    return this.ampTrackCtx?.value ?? null;
  }

  constructor(
    public dialog: MatDialog,
    private trackingContext: TrackingContext,
    private trackingService: TrackingService,
    @Optional() @SkipSelf() private readonly ampTrackCtx: AmpTrackCtx,
  ) { }

  exportOptions() {
    if (this.page) {
      const exporDialogRef = this.dialog.open(ExportDialogComponent, {
        data: {
          page: this.page,
          showUserFieldsColumn: this.showUserFieldsColumn,
          showContentColumn: this.showContentColumn,
          showOrganizationColumn: this.showOrganizationColumn,
          customFootnote: this.customFootnote,
          fileTypes: this.fileTypes,
        },
      });
      exporDialogRef.afterClosed().subscribe(exportOptions => {
        if (exportOptions) {
          this.exportHandler({
            ...exportOptions,
            params: this.params,
          });
          this.showExportProgress();
        }
        const context = this.trackingContext.context;
        const props = { ...context, ...{ selectedOption: exportOptions } };
        if (context?.name) {
          this.trackingService.track('', props);
        }
      });
    } else {
      this.exportHandler({
        params: this.params,
      } as ExportOptions);
      this.showExportProgress();
    }
  }

  showExportProgress() {
    if (this.exportProgress) {
      const progressData = { type: 'export', value: 0 };
      const progressDialog = this.dialog.open(ExportProgressDialogComponent, {
        width: '850px',
        disableClose: true,
        data: progressData,
      });

      this.exportProgress.subscribe(value => {
        progressData.value = value;
        if (value === 100) {
          setTimeout(() => {
            progressDialog.close();
            this.exportProgress.next(0);
          }, 500);
        }
      });
    }
  }
}
